import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDisclosure } from '@chakra-ui/hooks';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';

import Card from 'components/card';
import GoBack from 'components/actions/GoBack';
import InputField from 'components/fields/InputField';
import SearchSelector from 'components/fields/SearchSelector';
import { getDevis, updateDevis, sendDevisEmail, acceptDevis, refuseDevis, getDevisNote, getDevisProforma } from 'interfaces/devis';
import { getProductsByIds } from 'interfaces/products';
import OrderTable from 'views/orders/createOrders/components/OrderTable';
import AddProductsModal from 'views/orders/createOrders/components/AddProductModal';
import { getTotalHT, getTotalTTC, getGlobalDiscountAmount, isCustomerHT } from 'views/orders/createOrders/utils';
import { MdAddBox, MdPictureAsPdf, MdDownload } from 'react-icons/md';

const STATUS_LABELS: Record<string, string> = {
    draft: 'Brouillon',
    sent: 'Envoyé',
    accepted: 'Accepté',
    refused: 'Refusé',
};

const STATUS_COLORS: Record<string, string> = {
    draft: 'bg-gray-200 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-200 text-green-700',
    refused: 'bg-red-100 text-red-700',
};

const ConfirmModal = (props: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    text: string;
    confirmLabel: string;
    confirmClass: string;
}) => {
    const { isOpen, onClose, onConfirm, title, text, confirmLabel, confirmClass } = props;
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto !w-max min-w-[350px] !max-w-[85%] top-[12vh]">
                <ModalBody>
                    <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[650px] flex flex-col !z-[1004]">
                        <h1 className="mb-[20px] text-2xl font-bold">{title}</h1>
                        <p>{text}</p>
                        <div className="mt-5 flex gap-2 mx-auto">
                            <button
                                onClick={onClose}
                                className="linear rounded-xl border-2 border-gray-700 px-5 py-3 text-base font-medium text-gray-700 transition duration-200 hover:bg-gray-600/5 active:bg-gray-700/5"
                            >
                                Annuler
                            </button>
                            <button
                                className={`linear col-span-12 ml-3 flex items-center justify-center rounded-xl px-3 py-3 text-base font-medium text-white ${confirmClass}`}
                                onClick={onConfirm}
                            >
                                {confirmLabel}
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

const EditDevis = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [devis, setDevis] = useState<any>(null);
    const [products, setProducts] = useState<any>([]);
    const [sending, setSending] = useState(false);
    const [pdfLoading, setPdfLoading] = useState<'view' | 'download' | null>(null);
    const [proformaLoading, setProformaLoading] = useState<'view' | 'download' | null>(null);
    const {
        isOpen: isOpenAccept,
        onOpen: onOpenAccept,
        onClose: onCloseAccept,
    } = useDisclosure();
    const {
        isOpen: isOpenRefuse,
        onOpen: onOpenRefuse,
        onClose: onCloseRefuse,
    } = useDisclosure();
    const {
        isOpen: isOpenAddProduct,
        onOpen: onOpenAddProduct,
        onClose: onCloseAddProduct,
    } = useDisclosure();

    const isEditable = devis && (devis.status === 'draft' || devis.status === 'sent');

    const fetchDevis = async () => {
        const res = await getDevis(params.id as string);
        if (!res) return;
        setDevis(res);
        const productIds = (res.items || [])
            .map((i: any) => i.productId)
            .filter(Boolean);
        if (productIds.length) {
            const fetchedProducts = await getProductsByIds(productIds);
            if (fetchedProducts) setProducts(fetchedProducts);
        }
    };

    useEffect(() => {
        fetchDevis();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id]);

    const handleChange = (e: any) => {
        setDevis((prev: any) => ({ ...prev, [e.target.id]: e.target.value }));
    };

    const globalDiscount = (d: any) => ({
        globalDiscountType: d?.globalDiscountType,
        globalDiscountValue: d?.globalDiscountValue,
    });

    const updateGlobalDiscountType = (value: string) => {
        const updatedDevis = {
            ...devis,
            globalDiscountType: value,
            globalDiscountValue: value === 'none' ? 0 : devis.globalDiscountValue,
        };
        updatedDevis.totalPrice = getTotalHT(updatedDevis.items, globalDiscount(updatedDevis));
        setDevis(updatedDevis);
    };

    const updateGlobalDiscountValue = (value: number) => {
        const parsed = parseFloat(String(value));
        const updatedDevis = { ...devis, globalDiscountValue: isNaN(parsed) ? 0 : parsed };
        updatedDevis.totalPrice = getTotalHT(updatedDevis.items, globalDiscount(updatedDevis));
        setDevis(updatedDevis);
    };

    const updateQuantity = (product: any, value: number) => {
        const newItems = devis.items.map((item: any) =>
            item === product ? { ...item, quantity: parseFloat(String(value)) || 0 } : item,
        );
        setDevis({ ...devis, items: newItems, totalPrice: getTotalHT(newItems, globalDiscount(devis)) });
    };

    const updateUnityPrice = (product: any, value: number) => {
        const parsed = parseFloat(String(value));
        const newItems = devis.items.map((item: any) =>
            item === product ? { ...item, price: isNaN(parsed) ? 0 : parsed } : item,
        );
        setDevis({ ...devis, items: newItems, totalPrice: getTotalHT(newItems, globalDiscount(devis)) });
    };

    const updateDiscount = (product: any, value: number) => {
        const parsed = parseFloat(String(value));
        const newItems = devis.items.map((item: any) =>
            item === product ? { ...item, discountValue: isNaN(parsed) ? 0 : parsed } : item,
        );
        setDevis({ ...devis, items: newItems, totalPrice: getTotalHT(newItems, globalDiscount(devis)) });
    };

    const updateDiscountType = (product: any, value: string) => {
        const newItems = devis.items.map((item: any) =>
            item === product
                ? { ...item, discountType: value, discountValue: value === 'none' ? 0 : item.discountValue }
                : item,
        );
        setDevis({ ...devis, items: newItems, totalPrice: getTotalHT(newItems, globalDiscount(devis)) });
    };

    const updateComment = (product: any, value: string) => {
        const newItems = devis.items.map((item: any) =>
            item === product ? { ...item, comment: value } : item,
        );
        setDevis({ ...devis, items: newItems });
    };

    const updateLotNumber = (product: any, value: string) => {
        const newItems = devis.items.map((item: any) =>
            item === product ? { ...item, lotNumber: value } : item,
        );
        setDevis({ ...devis, items: newItems });
    };

    const updatePackaging = (product: any, packaging: any) => {
        const newItems = devis.items.map((item: any) => {
            if (item !== product) return item;
            if (packaging) {
                return {
                    ...item,
                    packagingRef: packaging.ref,
                    packagingUnity: packaging.unity,
                    packagingWeight: packaging.weight,
                    packagingBillByBaseUnit: packaging.billByBaseUnit || false,
                    price: parseFloat(packaging.price),
                };
            }
            return item;
        });
        setDevis({ ...devis, items: newItems, totalPrice: getTotalHT(newItems, globalDiscount(devis)) });
    };

    const deleteItem = (item: any) => {
        const newItems = devis.items.filter((i: any) => i.productId !== item.productId);
        setDevis({ ...devis, items: newItems, totalPrice: getTotalHT(newItems, globalDiscount(devis)) });
    };

    const addItems = (newItems: any) => {
        const items = [...devis.items, ...newItems];
        setDevis({ ...devis, items, totalPrice: getTotalHT(items, globalDiscount(devis)) });
        const alreadyLoaded = new Set((products ?? []).map((p: any) => p._id));
        const toFetch = newItems.map((i: any) => i.productId).filter((id: string) => id && !alreadyLoaded.has(id));
        if (toFetch.length) {
            getProductsByIds(toFetch).then((fetched) => {
                if (fetched?.length) setProducts((prev: any) => [...(prev ?? []), ...fetched]);
            });
        }
        onCloseAddProduct();
    };

    const handleSave = async () => {
        const parsedDiscount = parseFloat(String(devis.globalDiscountValue));
        const res = await updateDevis({
            ...devis,
            globalDiscountValue: isNaN(parsedDiscount) ? 0 : parsedDiscount,
        });
        if (res.error) {
            toast.error(res.error);
            return;
        }
        toast.success('Devis mis à jour');
        fetchDevis();
    };

    const handleViewPdf = async () => {
        setPdfLoading('view');
        const blob = await getDevisNote(devis._id);
        setPdfLoading(null);
        if (!blob) {
            toast.error('Impossible de générer le PDF du devis');
            return;
        }
        window.open(window.URL.createObjectURL(blob), '_blank');
    };

    const handleDownloadPdf = async () => {
        setPdfLoading('download');
        const blob = await getDevisNote(devis._id);
        setPdfLoading(null);
        if (!blob) {
            toast.error('Impossible de générer le PDF du devis');
            return;
        }
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `devis-${devis.devisNumber}.pdf`;
        link.click();
    };

    const handleViewProforma = async () => {
        setProformaLoading('view');
        const blob = await getDevisProforma(devis._id);
        setProformaLoading(null);
        if (!blob) {
            toast.error('Impossible de générer la facture proforma');
            return;
        }
        window.open(window.URL.createObjectURL(blob), '_blank');
    };

    const handleDownloadProforma = async () => {
        setProformaLoading('download');
        const blob = await getDevisProforma(devis._id);
        setProformaLoading(null);
        if (!blob) {
            toast.error('Impossible de générer la facture proforma');
            return;
        }
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `facture-proforma-${devis.devisNumber}.pdf`;
        link.click();
    };

    const handleSend = async () => {
        setSending(true);
        const res = await sendDevisEmail(devis._id);
        setSending(false);
        if (!res?.error) {
            toast.success('Devis envoyé au client');
            fetchDevis();
        }
    };

    const handleAccept = async () => {
        const res = await acceptDevis(devis._id);
        onCloseAccept();
        if (!res?.error) {
            toast.success('Devis accepté, commande générée');
            fetchDevis();
        }
    };

    const handleRefuse = async () => {
        const res = await refuseDevis(devis._id);
        onCloseRefuse();
        if (!res?.error) {
            toast.success('Devis marqué comme refusé');
            fetchDevis();
        }
    };

    if (!devis) {
        return (
            <div className="mt-3 px-2 sm:px-4 lg:mt-0 lg:px-0">
                <Card extra="w-full min-h-[300px] !p-5 border" />
            </div>
        );
    }

    const customerCompany = devis.customer?.company || devis.customer?.displayName;

    return (
        <div className="mt-3 px-2 sm:px-4 lg:mt-0 lg:px-0">
            <ConfirmModal
                isOpen={isOpenAccept}
                onClose={onCloseAccept}
                onConfirm={handleAccept}
                title="Accepter ce devis"
                text="Le devis sera transformé en commande en cours pour ce client. Voulez-vous continuer ?"
                confirmLabel="Marquer accepté"
                confirmClass="bg-green-500 hover:bg-green-600"
            />
            <ConfirmModal
                isOpen={isOpenRefuse}
                onClose={onCloseRefuse}
                onConfirm={handleRefuse}
                title="Refuser ce devis"
                text="Ce devis sera marqué comme refusé et ne pourra plus être modifié."
                confirmLabel="Marquer refusé"
                confirmClass="bg-red-500 hover:bg-red-600"
            />
            <AddProductsModal
                isOpen={isOpenAddProduct}
                onClose={onCloseAddProduct}
                addProducts={addItems}
                existingProducts={devis.items.map((item: any) => item._id)}
                customerId={devis.customer?._id}
            />

            <GoBack />

            <Card extra="mt-3 w-full !p-3 sm:!p-5 border">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h4 className="text-lg sm:text-2xl font-bold text-navy-700 dark:text-white">
                            Devis #{devis.devisNumber}
                        </h4>
                        <p
                            className="text-toola-500 cursor-pointer hover:underline text-sm"
                            onClick={() => window.open(`/customer/${devis.customer?._id}`, '_blank')}
                        >
                            {customerCompany}
                        </p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full ${STATUS_COLORS[devis.status]}`}>
                        {STATUS_LABELS[devis.status] || devis.status}
                    </span>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                    <button
                        className="linear flex items-center gap-2 justify-center rounded-xl border border-[#01499b] px-3 py-1.5 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 disabled:opacity-50"
                        onClick={handleViewPdf}
                        disabled={pdfLoading !== null}
                    >
                        <MdPictureAsPdf size={14} />
                        {pdfLoading === 'view' ? 'Génération...' : 'Voir le PDF'}
                    </button>
                    <button
                        className="linear flex items-center gap-2 justify-center rounded-xl border border-[#01499b] px-3 py-1.5 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 disabled:opacity-50"
                        onClick={handleDownloadPdf}
                        disabled={pdfLoading !== null}
                    >
                        <MdDownload size={14} />
                        {pdfLoading === 'download' ? 'Génération...' : 'Télécharger le PDF'}
                    </button>
                    <span className="mx-1 hidden sm:block border-l border-gray-300" />
                    <button
                        className="linear flex items-center gap-2 justify-center rounded-xl border border-gray-500 px-3 py-1.5 text-xs text-gray-600 transition duration-200 hover:bg-gray-500/5 disabled:opacity-50"
                        onClick={handleViewProforma}
                        disabled={proformaLoading !== null}
                        title="Document à titre indicatif, sans valeur comptable ni fiscale"
                    >
                        <MdPictureAsPdf size={14} />
                        {proformaLoading === 'view' ? 'Génération...' : 'Voir la facture proforma'}
                    </button>
                    <button
                        className="linear flex items-center gap-2 justify-center rounded-xl border border-gray-500 px-3 py-1.5 text-xs text-gray-600 transition duration-200 hover:bg-gray-500/5 disabled:opacity-50"
                        onClick={handleDownloadProforma}
                        disabled={proformaLoading !== null}
                        title="Document à titre indicatif, sans valeur comptable ni fiscale"
                    >
                        <MdDownload size={14} />
                        {proformaLoading === 'download' ? 'Génération...' : 'Télécharger la facture proforma'}
                    </button>
                </div>

                {devis.status === 'accepted' && devis.convertedOrderId && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700">
                            Ce devis a été accepté et transformé en commande.{' '}
                            <span
                                className="underline cursor-pointer font-medium"
                                onClick={() =>
                                    navigate(
                                        `/order/${
                                            typeof devis.convertedOrderId === 'string'
                                                ? devis.convertedOrderId
                                                : devis.convertedOrderId._id
                                        }`,
                                    )
                                }
                            >
                                Voir la commande générée
                            </span>
                        </p>
                    </div>
                )}

                <div className="mt-4">
                    <OrderTable
                        order={devis}
                        handleQtyChange={updateQuantity}
                        handlePriceChange={updateUnityPrice}
                        handleLotNumberChange={updateLotNumber}
                        handleDiscountChange={updateDiscount}
                        handleCommentChange={updateComment}
                        handleDiscountTypeChange={updateDiscountType}
                        handlePackagingChange={updatePackaging}
                        products={products}
                        onDelete={deleteItem}
                        customerId={devis.customer?._id}
                    />
                    {isEditable && (
                        <div className="mt-2 flex gap-1">
                            <MdAddBox size={15} className="flex my-auto text-toola-500" />
                            <p
                                className="flex text-xs sm:text-sm my-auto text-toola-500 hover:cursor-pointer hover:text-toola-600"
                                onClick={onOpenAddProduct}
                            >
                                Ajouter un produit
                            </p>
                        </div>
                    )}
                </div>

                <div className="mt-4 flex items-center gap-2 px-1">
                    <p className="text-base text-navy-700 dark:text-white whitespace-nowrap">Réduction globale:</p>
                    <div className="w-[100px]">
                        <SearchSelector
                            displayKey="name"
                            value={devis.globalDiscountType || 'none'}
                            options={[
                                { name: 'Aucune', _id: 'none' },
                                { name: '%', _id: 'percent' },
                                { name: '€', _id: 'value' },
                            ]}
                            onSelect={(e: any) => updateGlobalDiscountType(e._id)}
                            inputSmall={true}
                            disabled={!isEditable}
                        />
                    </div>
                    {devis.globalDiscountType && devis.globalDiscountType !== 'none' && (
                        <div className="flex items-center gap-1">
                            <InputField
                                extra="w-[70px]"
                                placeholder="0"
                                id="globalDiscountValue"
                                type="number"
                                value={devis.globalDiscountValue}
                                onChange={(e: any) => updateGlobalDiscountValue(e.target.value)}
                                inputSmall={true}
                                disabled={!isEditable}
                            />
                            <span className="text-xs text-gray-600">
                                {devis.globalDiscountType === 'percent' ? '%' : '€'}
                            </span>
                        </div>
                    )}
                </div>

                <div className="mt-4 flex flex-wrap gap-3 sm:gap-6 px-1">
                    <div className="flex items-center gap-2">
                        <p className="text-base text-navy-700 dark:text-white">Total HT:</p>
                        <p className="text-lg font-bold text-navy-700 dark:text-white">
                            {(devis.totalPrice || 0).toFixed(2)}€
                        </p>
                    </div>
                    {getGlobalDiscountAmount(devis.items || [], devis.globalDiscountType, devis.globalDiscountValue) > 0 && (
                        <div className="flex items-center gap-2">
                            <p className="text-base text-navy-700 dark:text-white">Réduction globale:</p>
                            <p className="text-lg font-bold text-navy-700 dark:text-white">
                                -{getGlobalDiscountAmount(devis.items || [], devis.globalDiscountType, devis.globalDiscountValue).toFixed(2)}€
                            </p>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <p className="text-base text-navy-700 dark:text-white">Total TTC:</p>
                        <p className="text-lg font-bold text-navy-700 dark:text-white">
                            {getTotalTTC(devis.items || [], { isCustomerHT: isCustomerHT(devis.customer), ...globalDiscount(devis) }).toFixed(2)}€
                        </p>
                    </div>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3">
                    <div className="col-span-12 lg:col-span-4">
                        <InputField
                            label="Date de validité"
                            id="validUntil"
                            type="date"
                            value={devis.validUntil ? devis.validUntil.substring(0, 10) : ''}
                            onChange={handleChange}
                            disabled={!isEditable}
                            inputSmall={true}
                        />
                    </div>
                    <div className="col-span-12 lg:col-span-4">
                        <InputField
                            label="Date de livraison souhaitée"
                            id="deliveryDate"
                            type="date"
                            value={devis.deliveryDate ? devis.deliveryDate.substring(0, 10) : ''}
                            onChange={handleChange}
                            disabled={!isEditable}
                            inputSmall={true}
                        />
                    </div>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="col-span-1">
                        <InputField
                            label="Note du client"
                            id="customerNotes"
                            type="text"
                            value={devis.customerNotes || ''}
                            onChange={handleChange}
                            disabled={!isEditable}
                            inputSmall={true}
                        />
                    </div>
                    <div className="col-span-1">
                        <InputField
                            label="Note interne"
                            id="internalNotes"
                            type="text"
                            value={devis.internalNotes || ''}
                            onChange={handleChange}
                            disabled={!isEditable}
                            inputSmall={true}
                        />
                    </div>
                </div>

                {isEditable && (
                    <div className="mt-5 flex flex-wrap gap-3">
                        <button
                            className="linear flex items-center justify-center rounded-xl px-6 py-2 text-sm font-medium text-white bg-[#0260CB] hover:bg-[#01499b]"
                            onClick={handleSave}
                        >
                            Enregistrer
                        </button>
                        <button
                            className="linear flex items-center justify-center rounded-xl px-6 py-2 text-sm font-medium text-white bg-toola-500 hover:bg-toola-600 disabled:opacity-50"
                            onClick={handleSend}
                            disabled={sending}
                        >
                            {sending ? 'Envoi...' : devis.status === 'sent' ? 'Renvoyer au client' : 'Envoyer au client'}
                        </button>
                        <button
                            className="linear flex items-center justify-center rounded-xl px-6 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600"
                            onClick={onOpenAccept}
                        >
                            Marquer accepté
                        </button>
                        <button
                            className="linear flex items-center justify-center rounded-xl px-6 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600"
                            onClick={onOpenRefuse}
                        >
                            Marquer refusé
                        </button>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default EditDevis;
